import * as React from 'react';
import * as RechartsPrimitive from 'recharts';
import { cn } from '../../lib/utils';

export type ChartConfig = {
  [key: string]: {
    label?: React.ReactNode;
    color?: string;
  };
};

const ChartContext = React.createContext<{ config: ChartConfig } | null>(null);

function useChart() {
  const context = React.useContext(ChartContext);
  if (!context) throw new Error('useChart must be used within a <ChartContainer />');
  return context;
}

function ChartContainer({
  id,
  className,
  children,
  config,
  style,
  ...props
}: React.ComponentProps<'div'> & {
  config: ChartConfig;
  children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>['children'];
}) {
  const uniqueId = React.useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, '')}`;
  const contextValue = React.useMemo(() => ({ config }), [config]);
  const colorVariables = React.useMemo(() => Object.fromEntries(
    Object.entries(config)
      .filter(([, item]) => item.color)
      .map(([key, item]) => [`--color-${key}`, item.color])
  ) as React.CSSProperties, [config]);

  return (
    <ChartContext.Provider value={contextValue}>
      <div
        data-chart={chartId}
        style={{ ...colorVariables, ...style }}
        className={cn(
          'flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-zinc-500 [&_.recharts-cartesian-grid_line]:stroke-zinc-800 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-zinc-700 [&_.recharts-layer]:outline-none [&_.recharts-surface]:outline-none',
          className
        )}
        {...props}
      >
        <RechartsPrimitive.ResponsiveContainer>
          {children}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
}

const ChartTooltip = RechartsPrimitive.Tooltip;

function ChartTooltipContent({
  active,
  payload,
  label,
  className
}: {
  active?: boolean;
  payload?: Array<{ dataKey?: string | number; name?: string | number; color?: string; value?: unknown }>;
  label?: unknown;
  className?: string;
}) {
  const { config } = useChart();
  if (!active || !payload?.length) return null;

  return (
    <div className={cn('grid min-w-32 gap-1.5 rounded-lg border border-zinc-800 bg-zinc-950/95 px-3 py-2 text-xs shadow-xl backdrop-blur-md', className)}>
      {label ? <div className="font-medium text-zinc-300">{String(label)}</div> : null}
      <div className="grid gap-1">
        {payload.map((item) => {
          const key = String(item.dataKey || item.name || 'value');
          const itemConfig = config[key];
          const color = item.color || itemConfig?.color || '#fff';
          return (
            <div key={key} className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-zinc-400">
                <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: color }} />
                {itemConfig?.label || item.name || key}
              </span>
              <span className="font-mono font-medium tabular-nums text-white">
                {typeof item.value === 'number' ? item.value.toLocaleString('en-US') : String(item.value ?? '')}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { ChartContainer, ChartTooltip, ChartTooltipContent };
